import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  CARD_BRAND_LABELS,
  cardBrandSchema,
  type AccountsResponse,
  type CardBrand,
  type CreateCardBody,
  type CreditCard,
  type UpdateCardBody,
} from '@lira/shared';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { toast } from '@/stores/toast';
import {
  BankPicker,
  CategoryIcon,
  ColorSwatches,
  MoneyInput,
  Select,
  Sheet,
  resolveIcon,
  type SelectOption,
} from '@/components/ui';

interface CardFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Cartão a editar; ausente = criação. */
  card?: CreditCard;
}

const DEFAULT_COLOR = '#00796B';

/** Opções de bandeira na ordem do contrato. */
const BRAND_OPTIONS: SelectOption<CardBrand>[] = cardBrandSchema.options.map((brand) => ({
  value: brand,
  label: CARD_BRAND_LABELS[brand],
}));

/** Dias 1..31 p/ os seletores de fechamento/vencimento. */
const DAY_OPTIONS: SelectOption<number>[] = Array.from({ length: 31 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}));

interface FormState {
  name: string;
  brand: CardBrand;
  icon: string;
  limitCents: number;
  closingDay: number;
  dueDay: number;
  linkedAccountId: string;
  color: string;
}

function initialState(card?: CreditCard): FormState {
  return {
    name: card?.name ?? '',
    brand: card?.brand ?? 'other',
    icon: card?.icon ?? '',
    limitCents: card?.limitCents ?? 0,
    closingDay: card?.closingDay ?? 1,
    dueDay: card?.dueDay ?? 10,
    linkedAccountId: card?.linkedAccountId ?? '',
    color: card?.color ?? DEFAULT_COLOR,
  };
}

/** Formulário de cartão (Sheet): limite teal, bandeira, conta de pagamento, ciclo e cor. */
export function CardFormModal({ open, onClose, card }: CardFormModalProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => initialState(card));

  useEffect(() => {
    if (open) setForm(initialState(card));
  }, [open, card]);

  const accountsQuery = useQuery({
    queryKey: ['accounts', 'compose'],
    queryFn: () => apiGet<AccountsResponse>('/accounts'),
    enabled: open,
  });
  const accountOptions = useMemo<SelectOption<string>[]>(
    () =>
      (accountsQuery.data?.accounts ?? [])
        .filter((a) => !a.archived)
        .map((a) => ({ value: a.id, label: a.name, icon: resolveIcon(a.icon), color: a.color })),
    [accountsQuery.data],
  );

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const mutation = useMutation({
    mutationFn: (body: FormState) => {
      const payload: CreateCardBody = {
        name: body.name.trim(),
        brand: body.brand,
        icon: body.icon,
        limitCents: body.limitCents,
        closingDay: body.closingDay,
        dueDay: body.dueDay,
        linkedAccountId: body.linkedAccountId,
        color: body.color,
      };
      if (card) {
        return apiPatch<{ card: CreditCard }>(`/cards/${card.id}`, payload as UpdateCardBody);
      }
      return apiPost<{ card: CreditCard }>('/cards', payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cards'] });
      void queryClient.invalidateQueries({ queryKey: ['expense-calendar'] });
      toast(card ? 'Cartão atualizado.' : 'Cartão criado.', 'success');
      onClose();
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const canSave =
    form.name.trim().length > 0 &&
    form.limitCents > 0 &&
    form.linkedAccountId.length > 0 &&
    !mutation.isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSave) return;
    mutation.mutate(form);
  };

  return (
    <Sheet open={open} onClose={onClose} title={card ? 'Editar cartão' : 'Novo cartão'}>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Limite — gigante, teal */}
        <div className="rounded-lg bg-cards/10 px-4 py-4">
          <label htmlFor="card-limit" className="mb-0.5 block text-[13px] text-muted">
            Limite
          </label>
          <MoneyInput
            id="card-limit"
            valueCents={form.limitCents}
            onChange={(cents) => update('limitCents', cents)}
            accent="cards"
            size="lg"
            aria-label="Limite do cartão"
          />
        </div>

        <div>
          <label htmlFor="card-name" className="mb-1 block text-[13px] text-muted">
            Descrição
          </label>
          <input
            id="card-name"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            maxLength={60}
            placeholder="Ex.: Nubank, Inter Black"
            autoComplete="off"
            className="h-11 w-full rounded-lg border border-field bg-surface px-3 text-[15px] text-ink transition-colors focus:border-cards focus:outline-none"
          />
        </div>

        <div>
          <label className="mb-1 block text-[13px] text-muted">Bandeira</label>
          <Select<CardBrand>
            value={form.brand}
            options={BRAND_OPTIONS}
            onChange={(brand) => update('brand', brand)}
            aria-label="Bandeira"
          />
        </div>

        <div>
          <div className="mb-2 flex items-center gap-3">
            <CategoryIcon icon={form.icon} color={form.color} size={44} fallbackText={form.name} />
            <label className="block text-[13px] text-muted">Banco / ícone</label>
          </div>
          <BankPicker
            value={form.icon}
            onChange={(icon, brandColor) =>
              setForm((f) => ({ ...f, icon, color: brandColor ?? f.color }))
            }
          />
        </div>

        <div>
          <label className="mb-1 block text-[13px] text-muted">Conta de pagamento</label>
          <Select<string>
            value={form.linkedAccountId}
            options={accountOptions}
            onChange={(id) => update('linkedAccountId', id)}
            placeholder="Selecione a conta"
            aria-label="Conta de pagamento"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[13px] text-muted">Dia de fechamento</label>
            <Select<number>
              value={form.closingDay}
              options={DAY_OPTIONS}
              onChange={(day) => update('closingDay', day)}
              aria-label="Dia de fechamento"
            />
          </div>
          <div>
            <label className="mb-1 block text-[13px] text-muted">Dia do vencimento</label>
            <Select<number>
              value={form.dueDay}
              options={DAY_OPTIONS}
              onChange={(day) => update('dueDay', day)}
              aria-label="Dia do vencimento"
            />
          </div>
        </div>

        <div>
          <label className="mb-2 block text-[13px] text-muted">Cor do cartão</label>
          <ColorSwatches value={form.color} onChange={(color) => update('color', color)} />
        </div>

        {mutation.isError && <p className="text-sm text-expense">{errorMessage(mutation.error)}</p>}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-full px-5 text-sm font-semibold uppercase tracking-wide text-muted transition-colors hover:bg-canvas hover:text-ink"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="flex h-11 items-center gap-2 rounded-full bg-cards px-6 text-sm font-semibold uppercase tracking-wide text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </form>
    </Sheet>
  );
}
